import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  CompareArrows as UnifyIcon,
  Add as AddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { vendedorService, Vendedor } from '../../services/vendedor';

const VendedorTable: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedVendedor, setSelectedVendedor] = useState<Vendedor | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Buscar vendedores
  const { data: vendedores = [], isLoading, error } = useQuery({
    queryKey: ['vendedores'],
    queryFn: () => vendedorService.getTableVendedores(),
    staleTime: 0,
    gcTime: 0,
  });

  // Filtrar vendedores baseado na busca
  const filteredVendedores = useMemo(() => {
    if (!search.trim()) return vendedores;

    const searchLower = search.toLowerCase();
    const searchDigits = normalizeDigits(search);
    return vendedores.filter((vendedor) =>
      vendedor.id?.toString().includes(searchLower) ||
      vendedor.nome?.toLowerCase().includes(searchLower) ||
      vendedor.cpf?.includes(searchLower) ||
      vendedor.praca?.toLowerCase().includes(searchLower) ||
      vendedor.situacao?.toLowerCase().includes(searchLower) ||
      (searchDigits && normalizeDigits(vendedor.telefone || vendedor.telefone1).includes(searchDigits))
    );
  }, [vendedores, search]);

  // Paginação dos dados filtrados
  const paginatedVendedores = useMemo(() => {
    return filteredVendedores.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredVendedores, page, rowsPerPage]);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, vendedor: Vendedor) => {
    setAnchorEl(event.currentTarget);
    setSelectedVendedor(vendedor);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedVendedor(null);
  };

  const handleEdit = () => {
    if (selectedVendedor) {
      navigate(`/vendedores/editar/${selectedVendedor.id}`);
      handleMenuClose();
    }
  };

  const handleView = () => {
    if (selectedVendedor) {
      navigate(`/vendedores/visualizar/${selectedVendedor.id}`);
      handleMenuClose();
    }
  };

  const handleUnify = () => {
    if (selectedVendedor) {
      navigate(`/vendedores/unificar/${selectedVendedor.id}`);
      handleMenuClose();
    }
  };

  const handleNew = () => {
    navigate('/vendedores/novo');
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatCPF = (cpf: string) => {
    if (!cpf) return '';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  function normalizeDigits(value: string) {
    return value ? value.replace(/\D/g, '') : '';
  }

  function formatTelefone(telefone: string) {
    const cleaned = normalizeDigits(telefone);
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return telefone || '';
  }

  const getSituationColor = (situacao: string) => {
    const situacaoUpper = situacao?.toUpperCase() || '';
    if (situacaoUpper === 'ATIVO') {
      return 'success';
    }
    if (situacaoUpper.includes('BLOQUEADO')) {
      return 'error';
    }
    return 'default';
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Erro ao carregar os dados dos vendedores.</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          placeholder="Filtrar"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 200, flex: 1 }}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
            }
          }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNew}
          sx={{ backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
        >
          Novo Vendedor
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Telefone</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>CPF</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Praça</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Situação</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Débito $</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedVendedores.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography sx={{ py: 4, color: 'text.secondary' }}>
                    {search ? 'Nenhum vendedor encontrado para esta busca.' : 'Nenhum vendedor cadastrado.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedVendedores.map((vendedor) => (
                <TableRow key={vendedor.id} hover>
                  <TableCell>{vendedor.id}</TableCell>
                  <TableCell>{vendedor.nome}</TableCell>
                  <TableCell>{formatTelefone(vendedor.telefone || vendedor.telefone1 || '')}</TableCell>
                  <TableCell>{formatCPF(vendedor.cpf)}</TableCell>
                  <TableCell>{vendedor.praca}</TableCell>
                  <TableCell>
                    <Chip
                      label={vendedor.situacao}
                      color={getSituationColor(vendedor.situacao)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatCurrency(vendedor.saldoDevedor || 0)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, vendedor)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl) && selectedVendedor?.id === vendedor.id}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={handleEdit}>
                        <EditIcon sx={{ mr: 1, fontSize: 20 }} />
                        Editar
                      </MenuItem>
                      <MenuItem onClick={handleView}>
                        <ViewIcon sx={{ mr: 1, fontSize: 20 }} />
                        Visualizar
                      </MenuItem>
                      <MenuItem onClick={handleUnify}>
                        <UnifyIcon sx={{ mr: 1, fontSize: 20 }} />
                        Unificar
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredVendedores.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} de ${count} itens`
        }
        sx={{ 
          borderTop: '1px solid',
          borderColor: 'divider',
          '.MuiTablePagination-toolbar': {
            pl: 2,
            pr: 2
          }
        }}
      />

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Typography>Carregando...</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default VendedorTable;
